// schema
import * as ISchema from "@schema";
// libraries
import React from "react";
import { connect } from "react-redux";
import pluralize from "pluralize";

// other internals
import { UserImage } from "@components/user-image";
import { A } from "@ui";
import { InfoTip } from "@components/tool-tip";
import { LogoClock } from "@components/logo";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import {
  AugmentedPerson,
  InviteAttendeesInput,
} from "../onboarding-org-invite/OnboardingOrgInvite";

// material-ui
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";

//////////////////
//TYPES
//////////////////
type InviteCheckboxProps = {
  people: AugmentedPerson[];
  isChecked?: boolean;
  eventName?: string;
  onCheck?: (attendeesInviteInput: InviteAttendeesInput[]) => void;
};

// Reused Tailwind classes
const rowClass = "cw-flex cw-items-center cw-justify-between";

//////////////////
// COMPONENT
//////////////////
const OrgInviteCheckboxCmpt = ({ people, isChecked, eventName, onCheck }: InviteCheckboxProps) => {
  const onAddInvitees = (checked: boolean) => {
    if (onCheck) {
      const invitees = [];
      for (let person of people) {
        invitees.push({
          calendarId: person.targetCalendarId,
          added: checked,
          isSuggested: person.isSuggested,
        });
      }
      onCheck(invitees);
    }
  };

  const renderInfoText = () => {
    if (Object.keys(people).length !== 1) return "";
    let info = "";
    const person = people[0];
    if (!!person.numberOfOrganizedMeetings) {
      info = `Because ${person.givenName} organizes ${person.numberOfOrganizedMeetings} of your meetings`;
      if (!!person.numberOfSharedMeetings) {
        const meetings = pluralize("meeting", person.numberOfSharedMeetings);
        info += ` and you share ${person.numberOfSharedMeetings} other ${meetings}`;
      }
    } else if (!!person.numberOfSharedMeetings) {
      const meetings = pluralize("meeting", person.numberOfSharedMeetings);
      info = `Because you share ${person.numberOfSharedMeetings} ${meetings} with ${person.givenName}`;
    } else if (person.inviteReason == ISchema.SuggestedOrgInviteReason.MeetsWithYourTeammates) {
      info = `Because ${person.givenName} meets with your teammates`;
    } else {
      info = `Because you share meetings with ${person.givenName}`;
    }
    return <span>{info}</span>;
  };

  const maybeRenderUserBadge = (isUser?: boolean, size?: number) => {
    if (!isUser) {
      return <span />;
    }

    const badgeStyle = {
      backgroundColor: "rgba(83, 147, 198, 1)",
      width: size && size > 50 ? 22 : 18,
      height: size && size > 50 ? 22 : 18,
      right: size && size > 60 ? 0 : -3,
      bottom: size && size > 60 ? 0 : -3,
    };
    const logoSize = badgeStyle.width - 4;

    return (
      <div
        className="cw-absolute cw-rounded-full cw-flex cw-justify-center cw-items-center cw-z-[2]"
        style={badgeStyle}
      >
        <LogoClock size={logoSize} fill={greys.white} />
      </div>
    );
  };

  const renderEventAction = () => {
    const tipTarget = <A style={{ fontSize: 13 }}>Suggested</A>;

    return (
      <div className={rowClass}>
        <InfoTip
          text="Because you share meetings with this group"
          targetElement={tipTarget}
          tipType={"info"}
        />
        <Checkbox
          color="primary"
          checked={isChecked}
          onChange={() => {
            onAddInvitees(!isChecked);
          }}
        />
      </div>
    );
  };

  const maybeRenderAction = () => {
    const tipTarget = <A style={{ fontSize: 13 }}>Suggested</A>;
    if (people.length > 1 || !people.length) return null;
    const person = people[0];

    if (person.isUser) {
      return <div className={`${rowClass} cw-pr-[18px]`}>Joined!</div>;
    }

    return (
      <div className={rowClass}>
        {person.isPending ? (
          <div className="cw-text-gray-500 cw-text-sm">Invited</div>
        ) : person.isSuggested ? (
          <InfoTip text={renderInfoText()} targetElement={tipTarget} tipType={"info"} />
        ) : (
          ""
        )}
        <Checkbox
          disabled={person.isPending}
          color="primary"
          checked={isChecked || person.isPending}
          onChange={() => {
            onAddInvitees(!isChecked);
          }}
        />
      </div>
    );
  };

  const getTooltip = (namesInTooltip: string[]) => {
    const pluralOther = namesInTooltip.length === 1 ? "other" : "others";
    return (
      <Tooltip
        title={
          <div>
            {namesInTooltip.map((name, i) => {
              return <div key={name + i}>{name}</div>;
            })}
          </div>
        }
      >
        <span className="cw-cursor-pointer cw-border-gray-300 cw-border-dashed cw-border-b">{`${namesInTooltip.length} ${pluralOther}`}</span>
      </Tooltip>
    );
  };

  const getListOfAttendees = () => {
    const peopleNames = people.map((p) => p.givenName);
    let numberOfTotalCharacters = 0;
    const namesToDisplay = [];
    const namesToNotDisplay = [];
    for (let name of peopleNames) {
      if (numberOfTotalCharacters + name.length < 15) {
        namesToDisplay.push(name);
        numberOfTotalCharacters = numberOfTotalCharacters + name.length;
      } else {
        namesToNotDisplay.push(name);
      }
    }
    if (!namesToNotDisplay.length) {
      return <div className="cw-text-sm cw-text-gray-500">{namesToDisplay.join(", ")}</div>;
    } else {
      return (
        <div className="cw-text-sm cw-text-gray-500">
          {namesToDisplay.join(", ")} and {getTooltip(namesToNotDisplay)}{" "}
        </div>
      );
    }
  };

  if (people.length > 1 && eventName) {
    return (
      <div>
        <div className="cw-py-[5px]">
          <div className={rowClass}>
            <div className={rowClass}>
              <div>
                <UserImage
                  imgUrl={null}
                  name={String(people.length)}
                  showFullName={true}
                  style={{ width: 34, height: 34 }}
                />
              </div>
              <div className="cw-ml-s10">
                <div className="cw-text-gray-800">{`${people.length} attendees of "${eventName}"`}</div>
                {getListOfAttendees()}
              </div>
            </div>
            {renderEventAction()}
          </div>
        </div>
      </div>
    );
  }
  const person = people[0];
  if (!person) return null;

  return (
    <div>
      <div className="cw-py-[5px]">
        <div className={rowClass}>
          <div className={rowClass}>
            <div className="cw-relative">
              <UserImage
                imgUrl={person.externalImageUrl}
                name={person.givenName}
                style={{ width: 34, height: 34 }}
              />
              {maybeRenderUserBadge(person.isUser, 34)}
            </div>
            <div className="cw-ml-s10">
              <div className="cw-text-gray-800">{`${person.givenName} ${person.familyName}`}</div>
              <div className="cw-text-sm cw-text-gray-500">{person.targetCalendarId}</div>
            </div>
          </div>
          {maybeRenderAction()}
        </div>
      </div>
    </div>
  );
};

//////////////////
// REDUX
//////////////////
export const OrgInviteCheckbox = connect()(OrgInviteCheckboxCmpt);
